import type { Api } from '@rialtic/api'
import type { RouteLocationNormalized } from 'vue-router'
import { useClaimLab } from '../stores/claimLab'
import { abortNavigation, useNuxtApp } from '#imports'

type Params = { connectorId: string }
type ConnectorInfo = {
  enabled: boolean
  workspaceEnabled: boolean
  workspaceId: string
}

export type ClaimLabMiddlewareDeps = {
  $auth0: ReturnType<typeof useNuxtApp>['$auth0']
  abortNavigation: typeof abortNavigation
  currentUser: { value: Api.AuthUserResponse['data'] | null }
  fetcher: typeof $fetch
  runtimeConfig: ReturnType<typeof useRuntimeConfig>
}

const createClaimLabMiddlewareDeps = (): ClaimLabMiddlewareDeps => {
  const nuxtApp = useNuxtApp()

  return {
    $auth0: nuxtApp.$auth0,
    abortNavigation,
    currentUser: useCurrentUser(),
    fetcher: $fetch,
    runtimeConfig: useRuntimeConfig(),
  }
}

export const runClaimLabMiddleware = async (
  to: RouteLocationNormalized & { params: Params },
  deps: ClaimLabMiddlewareDeps = createClaimLabMiddlewareDeps(),
) => {
  const { $auth0, currentUser, fetcher, runtimeConfig } = deps
  const { isAuthenticated, getAccessTokenSilently } = $auth0()

  if (!isAuthenticated.value) {
    return deps.abortNavigation()
  }

  const token = await getAccessTokenSilently()

  // Fetch user data if not already loaded
  if (!currentUser.value) {
    const userData = await fetcher<Api.AuthUserResponse>(
      '/api/claim-lab/auth-user',
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        // Bound the wait and retry transient upstream stalls: this fetch gates
        // the whole page render, so a never-resolving request would leave the
        // app stuck on a blank splash with no recovery.
        timeout: 8000,
        retry: 2,
        retryDelay: 500,
      },
    )
    currentUser.value = userData.data
  }

  const config = runtimeConfig.public
  const claimLabInfo = await fetcher<ConnectorInfo>(
    `/api/claim-lab/connector/${to.params.connectorId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        ...(config.WORKSPACE_ID
          ? {
              'x-rialtic-workspace-id': config.WORKSPACE_ID as string,
            }
          : {}),
      },
      // Same rationale as auth-user: this also gates the page render.
      timeout: 8000,
      retry: 2,
      retryDelay: 500,
    },
  )

  if (!claimLabInfo.enabled) {
    return deps.abortNavigation()
  }
  const claimLab = useClaimLab()

  claimLab.setWorkspaceId(claimLabInfo.workspaceId)
}

export default defineNuxtRouteMiddleware(async (to) => {
  if (!import.meta.client) {
    return
  }

  return runClaimLabMiddleware(
    to as RouteLocationNormalized & { params: Params },
  )
})
